import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Bell, Check, Users, MessageSquare } from 'lucide-react';
import { NotificationItem } from './notifications/NotificationItem';
import { useNotifications } from './notifications/useNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  general: { label: 'Notifications', icon: Bell, accent: 'purple' },
  messages: { label: 'Messages', icon: MessageSquare, accent: 'green' },
  communities: { label: 'Communautés', icon: Users, accent: 'cyan' },
};

/* Cloche — Brief 27 oct : rendu + logique partagés avec /app/notifications
   (components/notifications/*) pour éviter toute dérive entre les deux surfaces. */
const NotificationDropdown = ({ isOpen, onClose, anchorRef, category = 'general' }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const meta = CATEGORY_META[category];
  const { notifications, loading, unreadCount, joinDecisions, load, markAllAsRead, respondJoinRequest, open } =
    useNotifications({ category, onNavigate: onClose });

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        anchorRef?.current && !anchorRef.current.contains(event.target)
      ) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const HeaderIcon = meta.icon;
  const accentColor = meta.accent;

  return (
    <div
      ref={dropdownRef}
      className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-16 sm:top-full sm:mt-2 w-auto sm:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50"
      data-testid={`notifications-dropdown-${category}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center">
          <HeaderIcon className={`h-5 w-5 mr-2 text-${accentColor}-500`} />
          {meta.label}
          {unreadCount > 0 && (
            <span className={`ml-2 bg-${accentColor}-500 text-white text-xs px-2 py-0.5 rounded-full`}>{unreadCount}</span>
          )}
        </h3>
        {unreadCount > 0 && (
          <Button size="sm" variant="ghost" onClick={markAllAsRead} className={`text-${accentColor}-500 text-sm`} data-testid={`mark-all-read-${category}-btn`}>
            <Check className="h-4 w-4 mr-1" />Tout lire
          </Button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 border-${accentColor}-500`}></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-testid="notifications-empty">
            <HeaderIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>Aucune notification pour le moment</p>
            <p className="text-xs mt-1 opacity-80">Les réactions, demandes et réservations apparaîtront ici.</p>
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notification_id}
                notification={notification}
                accent={accentColor}
                onOpen={open}
                joinDecision={joinDecisions[notification.notification_id]}
                onRespondJoin={respondJoinRequest}
              />
            ))}
          </div>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-100 dark:border-gray-700 text-center">
          <Button
            variant="ghost" size="sm" className={`text-${accentColor}-500`}
            onClick={() => { navigate('/app/notifications'); onClose(); }}
            data-testid="notifications-see-all"
          >
            Voir toutes les notifications
          </Button>
        </div>
      )}
    </div>
  );
};

// Hook — polls /api/notifications/counts every 10s
export const useNotificationCounts = () => {
  const [counts, setCounts] = useState({ general: 0, messages: 0, communities: 0, sessions: 0 });

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/counts`, { withCredentials: true });
      setCounts({
        general: data.general || 0,
        messages: data.messages || 0,
        communities: data.communities || 0,
        // Brief Jun 15 (post Vague 2) §3 — pending bookings + private 1on1 invitations
        sessions: data.sessions || 0,
      });
    } catch {
      // ignore for unauthenticated
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 10000);
    // Sprint 1.0c V1.a-bis hotfix #5 (Bug D) — Slack-like instant decrement:
    // Messages.js dispatches `notifications:refresh` after the user reads a
    // conversation, so the sidebar badge updates without waiting for the next
    // 10s poll. Any other action (DM delete, etc.) can use the same event.
    const onRefresh = () => fetchCounts();
    window.addEventListener('notifications:refresh', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', onRefresh);
    };
  }, [fetchCounts]);

  return { counts, refresh: fetchCounts };
};

export default NotificationDropdown;
